import lodashMerge from 'lodash.merge';

const DEFAULT_PADDING = {
  paddingRight: '8px',
  paddingLeft: '8px'
};

export default function ColStyles({
  zIndex,
  breakpoints,
  componentDefaults,
  s: componentS,
  m: componentM,
  l: componentL,
  isRtl
}) {
  const { start, end } = defineStartAndEnd(isRtl);

  const colBase = {
    zIndex,
    direction: isRtl ? 'rtl' : 'ltr',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'block',
    flexShrink: 0,
    flexGrow: 0,
    flexBasis: 'auto',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'stretch',
    minHeight: 'inherit'
  };

  const responsiveCols = () => {
    const defaults = expandPadding(componentDefaults);
    const s = merge(defaults, componentS);
    const m = merge(s, componentM);
    const l = merge(m, componentL);

    function buildStyleAtBreakpoint(col) {
      return {
        ...col.flex,
        ...widthProperties(col.width, col.minWidth),
        [`margin${start}`]: convertWidthValue(col.offset),
        [`margin${end}`]: convertWidthValue(col.offsetEnd),
        ...(col.padding && {
          ...(isPaddingValue(col.padding.paddingTop)
            ? { paddingTop: `${col.padding.paddingTop}px` }
            : {}),
          ...(isPaddingValue(col.padding.paddingBottom)
            ? { paddingBottom: `${col.padding.paddingBottom}px` }
            : {}),
          ...(isPaddingValue(col.padding.paddingStart)
            ? { [`padding${start}`]: `${col.padding.paddingStart}px` }
            : {}),
          ...(isPaddingValue(col.padding.paddingEnd)
            ? { [`padding${end}`]: `${col.padding.paddingEnd}px` }
            : {})
        })
      };
    }

    return {
      ...DEFAULT_PADDING,
      ...buildStyleAtBreakpoint(defaults),
      [`@media (min-width: ${breakpoints.s})`]: buildStyleAtBreakpoint(s),
      [`@media (min-width: ${breakpoints.m})`]: buildStyleAtBreakpoint(m),
      [`@media (min-width: ${breakpoints.l})`]: buildStyleAtBreakpoint(l)
    };
  };

  return { ...colBase, ...responsiveCols() };
}

// helpers
function merge(...args) {
  return lodashMerge({}, ...args.map(obj => expandPadding(obj)));
}

function isPaddingValue(value) {
  return !!value || value === 0;
}

function expandPadding(col) {
  let expandedCol = col;
  const padding = expandedCol && formatPadding(expandedCol.padding);
  if (!!padding || padding === 0) {
    if (typeof padding === 'number') {
      expandedCol.padding = {
        paddingEnd: padding,
        paddingStart: padding
      };
    }
  }
  return expandedCol;
}

function formatPadding(padding) {
  if (typeof padding === 'number') {
    return padding;
  }
  if (typeof padding === 'object' && padding !== null) {
    const newPadding = padding;
    ['start', 'end', 'top', 'bottom'].forEach(property => {
      if (property in padding) {
        newPadding[
          `padding${property.slice(0, 1).toUpperCase() + property.slice(1)}`
        ] = padding[property];
        delete newPadding[property];
      }
    });
    return newPadding;
  }
}

function convertWidthValue(numWidth) {
  /* 
  minWidth="content"
  'max-content' is not supported in IE, and therefore cannot be used
  even though it works great in browsers real people use
  if (numWidth === 'content') {
    return 'max-content';
  } 
  */
  if (typeof numWidth === 'number') {
    if (numWidth > 0 && numWidth <= 1) {
      return `${numWidth * 100}%`;
    }
    return `${numWidth}px`;
  }
  return 0;
}

function widthProperties(numWidth, numMinWidth) {
  const minWidth = numMinWidth !== undefined && {
    minWidth: convertWidthValue(numMinWidth)
  };
  if (typeof numWidth === 'number') {
    if (numWidth > 0 && numWidth <= 1) {
      return { ...minWidth, width: `${numWidth * 100}%`, flexGrow: 0 };
    }
    return { ...minWidth, width: `${numWidth}px`, flexGrow: 0 };
  }
  if (numWidth === 'content') {
    return { ...minWidth, width: 'auto', flexGrow: 0, flexShrink: 0 };
  }
  // defaults to 'fill'
  return { ...minWidth, width: 0, flexGrow: 1 };
}

function defineStartAndEnd(isRtl) {
  return isRtl
    ? { start: 'Right', end: 'Left' }
    : { start: 'Left', end: 'Right' };
}
